import axios from 'axios';
import appSettings from '../config/appSettings';
import { getCurrentVersion, forceRefresh, getCurrentLanguage, setCurrentLanguage } from '../context/MetadataContext';
import storage from '../platform/storage';
import events from '../platform/events';
import { generateNonce, getUnixTimestamp, generateHmacSha256, createSignaturePayload } from '../utils/crypto';


const API_URL = appSettings.apiUrl;

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// the metadata response carries the version its own refresh is about to set
const METADATA_ENDPOINT = '/app/metadata';

const unauthenticatedEndpoints = [
  '/auth/register',
  '/auth/token',
  '/auth/link',
  '/auth/recover',
  '/auth/unlink',
  '/app/registration_data'
];

// add auth token and security headers to request
api.interceptors.request.use(
  async config => {
    // Add security headers if secret key is configured
    if (appSettings.appSecretKey) {
      try {
        // Generate nonce and timestamp
        const nonce = generateNonce();
        const timestamp = getUnixTimestamp();

        // Create the signature payload
        const payload = createSignaturePayload(config, nonce, timestamp);

        // Generate HMAC-SHA256 signature
        const signature = await generateHmacSha256(appSettings.appSecretKey, payload);

        // Add security headers
        config.headers['X-App-Signature'] = signature;
        config.headers['X-Nonce'] = nonce;
        config.headers['X-Timestamp'] = timestamp;
      } catch (error) {
        console.error('Failed to generate security headers:', error);
        // Continue without security headers if generation fails
      }
    }

    // Check if this endpoint requires authentication
    const isUnauthenticated = unauthenticatedEndpoints.some(endpoint =>
      config.url.includes(endpoint)
    );

    if (!isUnauthenticated) {
      // Get token from storage
      let token = await storage.getItem('authToken');

      // If no token exists, try to get one
      if (!token) {
        try {
          const userKey = await storage.getItem('userKey');
          const deviceUid = await storage.getItem('deviceUid');

          if (userKey && deviceUid) {
            await getAuthToken(); // This will store the token in storage
            token = await storage.getItem('authToken');
          }
        } catch (error) {
          console.error('Failed to get auth token:', error);
        }
      }

      // Attach token to request header
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add metadata version to authenticated requests
      const currentVersion = getCurrentVersion();
      if (currentVersion) {
        config.headers['x-metadata-version'] = currentVersion;
      }
    }

    return config;
  },
  error => Promise.reject(error)
);

// handle auth token expiration
api.interceptors.response.use(
  response => {
    // todo look for language change
    checkLanguage(response)
    // Check metadata version on successful responses. Not on the metadata
    // response itself: forceRefresh sets the version once it resolves, so a
    // check here would start a second, redundant refresh
    if (response.config?.url !== METADATA_ENDPOINT) {
      checkMetadata(response);
    }
    
    // Clear any existing errors on successful responses
    if (response.status >= 200 && response.status < 300) {
      if (globalClearError) {
        globalClearError();
      }
    }
  
    return response;
  },
  async error => {
    const originalRequest = error.config;
    const statusCode = error.response?.status;
    const responseErrorCode = error.response?.headers?.['x-error-code'] || error.response?.headers?.['X-Error-Code'];
    
    // FIRST: Handle normal system behaviors that aren't real errors
    
    // Handle 401 auth errors - these are normal, not real errors.
    // Only an expired or missing session (ERR_NOT_AUTHENTICATED) is
    // re-tokened; ERR_AUTH_FAILED / ERR_ACCOUNT_DELETED are final, and a 401
    // from /auth/* itself must not start another /auth/token call
    const isUnauthenticatedRequest = unauthenticatedEndpoints.some(endpoint =>
      originalRequest?.url?.includes(endpoint)
    );
    if (statusCode === 401 && responseErrorCode === 'ERR_NOT_AUTHENTICATED'
        && !isUnauthenticatedRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // a concurrent request may already have re-tokened: when the stored
        // token is not the one this request was sent with, retry with it
        // instead of making another /auth/token call
        const sentAuthorization = typeof originalRequest.headers?.get === 'function'
          ? originalRequest.headers.get('Authorization')
          : originalRequest.headers?.Authorization;
        let token = await storage.getItem('authToken');
        if (!token || sentAuthorization === `Bearer ${token}`) {
          console.log('Token expired, refreshing...');
          await getAuthToken();
          token = await storage.getItem('authToken');
        }

        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          const currentVersion = getCurrentVersion();
          if (currentVersion) {
            originalRequest.headers['x-metadata-version'] = currentVersion;
          }
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Only trigger error if token refresh fails
        if (globalTriggerError) {
          globalTriggerError({
            statusCode: 401,
            errorCode: 'ERR_AUTH_FAILED',
            visibility: 'system',
            originalError: refreshError
          });
        }
      }
      return Promise.reject(error);
    }
    
    // Handle metadata version errors - also normal system behavior
    const metadataErrorCode = error.response?.headers?.['x-error-code'] || error.response?.headers?.['X-Error-Code'];
    const isMetadataVersionError = (
      statusCode === 409 && metadataErrorCode === 'ERR_INVALID_METADATA_VERSION'
    );
    
    if (isMetadataVersionError && !originalRequest._metadataRetry) {
      originalRequest._metadataRetry = true;
      
      try {
        console.log('Invalid metadata version detected, refreshing metadata');
        await forceRefresh();
        
        const newVersion = getCurrentVersion();
        if (newVersion) {
          originalRequest.headers['x-metadata-version'] = newVersion;
          return api(originalRequest);
        }
      } catch (metadataError) {
        console.error('Metadata refresh failed:', metadataError);
        // Only trigger error if metadata refresh fails
        if (globalTriggerError) {
          globalTriggerError({
            statusCode: 409,
            errorCode: 'ERR_INVALID_METADATA_VERSION',
            visibility: 'system',
            originalError: metadataError
          });
        }
      }
      return Promise.reject(error);
    }
    
    // SECOND: Handle real errors with error headers
    const headers = error.response?.headers || {};
    const errorCode = headers['x-error-code'] || headers['X-Error-Code'];
    const errorVisibility = headers['x-error-visibility'] || headers['X-Error-Visibility'];
    
    if (errorCode && globalTriggerError) {
      globalTriggerError({
        statusCode,
        errorCode,
        visibility: errorVisibility,
        originalError: error
      });
      return Promise.reject(error);
    }
    
    // THIRD: Handle other errors without headers as fallback
    if (globalTriggerError) {
      globalTriggerError({
        statusCode: statusCode || 0,
        errorCode: `HTTP_${statusCode || 'UNKNOWN'}`,
        visibility: statusCode >= 500 ? 'system' : 'user',
        originalError: error
      });
    }
    
    return Promise.reject(error);
  }
);

export default api;


const checkLanguage = (response) => {
  const serverLanguage = response.headers['x-session-language']
  const currentLang = getCurrentLanguage();

  if (serverLanguage && serverLanguage !== currentLang) {
    console.log('Language change detected:', currentLang, '->', serverLanguage);
    setCurrentLanguage(serverLanguage);
  }
}

const checkMetadata = (response) => {

  // get the server version from the response header
  const serverVersion = response.headers['x-metadata-version'];
  const currentVersion = getCurrentVersion();
  
  // Only check if both versions exist and are different
  if (serverVersion && serverVersion !== currentVersion) {
    console.log(`Metadata version mismatch: local=${currentVersion}, server=${serverVersion}`);
    // fire-and-forget: a failed refresh is logged by MetadataContext and the
    // next mismatching response tries again
    forceRefresh().catch(() => {});
  }
};

// Error service - needs to be somewhere else
// Simple error manager for API layer
let globalTriggerError = null;
let globalClearError = null;

// Function to register error handlers from GlobalErrorContext
export const registerErrorHandlers = (triggerErrorFn, clearErrorFn) => {
  globalTriggerError = triggerErrorFn;
  globalClearError = clearErrorFn;
};

// the /auth/token request in flight: concurrent callers share it (a caller's
// payload is ignored while another request is in flight)
let authTokenInFlight = null;

export const getAuthToken = (payload) => {
  if (!authTokenInFlight) {
    authTokenInFlight = (async () => {
      try {
        return await requestAuthToken(payload);
      } finally {
        authTokenInFlight = null;
      }
    })();
  }
  return authTokenInFlight;
};

const requestAuthToken = async (payload) => {
  console.log('getAuthToken called')
  // Build request body from args or storage fallback
  let body = payload;
  if (!body) {
    const userKey   = await storage.getItem('userKey');
    const deviceUid = await storage.getItem('deviceUid');
    body = { user_key: userKey, device_uid: deviceUid };
  }

  const res = await api.post('/auth/token', body);

  // Persist token for subsequent requests via request-interceptor
  const token = res.data?.access_token;
  if (token) {
    await storage.setItem('authToken', token);
  }

  // Extract and store user data from auth response
  const { nickname, country_code, language_code, color_palette, color_mode } = res.data;
  
  if (nickname || country_code || language_code || color_palette || color_mode) {
    // Emit event to update UserContext
    events.emit('userDataUpdated', {
      nickname: nickname,
      countryCode: country_code,
      languageCode: language_code,
      theme: color_palette,  // Backend's color_palette maps to our theme
      lightMode: color_mode  // Backend's color_mode maps to our lightMode
    });
  }

  return res.data;
};

// SOLO MODE
export const soloGetStatus = () => api.get('/solo/status').then(res => res.data);
export const soloFindPuzzle = () => api.get('/solo/find').then(res => res.data);
export const soloStartPuzzle = () => api.post('/solo/start').then(res => res.data);
export const soloAbandonPuzzle = () => api.post('/solo/abandon').then(res => res.data);
export const soloSubmitSolution = () => api.post('/solo/submit').then(res => res.data);
export const soloRejectPuzzle = () => api.post('/solo/reject').then(res => res.data);
export const soloMakeMove = (x, y, value) => api.post('/solo/move', { x, y, value }).then(res => res.data);
export const soloUndoMove = () => api.post('/solo/undo').then(res => res.data);
export const soloEraseCell = (x, y) => api.post('/solo/move', { x, y, value: null }).then(res => res.data);
export const soloGetHint = () => api.post('/solo/hint').then(res => res.data);
export const soloSetNotes = (x, y, value) => api.post('/solo/note', { x, y, value }).then(res => res.data);
export const soloDeleteNotes = (x, y) => api.delete('/solo/note', { data: { x, y } }).then(res => res.data);
export const soloChallenge = () => api.post('/solo/challenge').then(res => res.data);
export const soloFindByShareCode = (shareCode) => api.get(`/solo/find/${shareCode}`).then(res => res.data);
export const soloGetLeaderboard = (category, params = {}) => {
  const { skip, limit, timeframe } = params;
  let url = `/solo/leaderboard`;
  const queryParams = new URLSearchParams();
  
  queryParams.append('category', category);
  if (timeframe) queryParams.append('timeframe', timeframe);
  if (skip !== undefined) queryParams.append('skip', skip);
  if (limit !== undefined) queryParams.append('limit', limit);
  
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }
  
  return api.get(url).then(res => res.data);
};

// DUEL MODE
export const duelGetStatus = () => api.get('/duel/status').then(res => res.data);
export const duelFindPuzzle = () => api.get('/duel/find').then(res => res.data);
export const duelFindByChallenge = (challengeCode) => api.get(`/duel/find/${challengeCode}`).then(res => res.data);
export const duelStartPuzzle = () => api.post('/duel/start').then(res => res.data);
export const duelAbandonPuzzle = () => api.post('/duel/abandon').then(res => res.data);
export const duelSubmitSolution = () => api.post('/duel/submit').then(res => res.data);
export const duelRejectPuzzle = () => api.post('/duel/reject').then(res => res.data);
export const duelMakeMove = (x, y, value) => api.post('/duel/move', { x, y, value }).then(res => res.data);
export const duelUndoMove = () => api.post('/duel/undo').then(res => res.data);
export const duelEraseCell = (x, y) => api.post('/duel/move', { x, y, value: null }).then(res => res.data);
export const duelGetHint = () => api.post('/duel/hint').then(res => res.data);
export const duelSetNotes = (x, y, value) => api.post('/duel/note', { x, y, value }).then(res => res.data);
export const duelDeleteNotes = (x, y) => api.delete('/duel/note', { data: { x, y } }).then(res => res.data);
export const duelAcceptChallenge = (challengeKey) => api.get(`/duel/accept/${challengeKey}`).then(res => res.data);
export const duelDeclineChallenge = (challengeKey) => api.get(`/duel/decline/${challengeKey}`).then(res => res.data);

// MIRROR MODE
export const mirrorGetStatus = () => api.get('/mirror/status').then(res => res.data);
export const mirrorFindPuzzle = () => api.get('/mirror/find').then(res => res.data);
export const mirrorStartPuzzle = () => api.post('/mirror/start').then(res => res.data);
export const mirrorAbandonPuzzle = () => api.post('/mirror/abandon').then(res => res.data);
export const mirrorSubmitSolution = () => api.post('/mirror/submit').then(res => res.data);
export const mirrorRejectPuzzle = () => api.post('/mirror/reject').then(res => res.data);
export const mirrorMakeMove = (x, y, value) => api.post('/mirror/move', { x, y, value }).then(res => res.data);
export const mirrorUndoMove = () => api.post('/mirror/undo').then(res => res.data);
export const mirrorEraseCell = (x, y) => api.post('/mirror/move', { x, y, value: null }).then(res => res.data);
export const mirrorGetHint = () => api.post('/mirror/hint').then(res => res.data);
export const mirrorSetNotes = (x, y, value) => api.post('/mirror/note', { x, y, value }).then(res => res.data);
export const mirrorDeleteNotes = (x, y) => api.delete('/mirror/note', { data: { x, y } }).then(res => res.data);

// TOURNAMENT MODE
export const tournamentGetStatus = () => api.get('/tournament/status').then(res => res.data);
export const tournamentFindPuzzle = (tournamentKey) => api.get(`/tournament/find/${tournamentKey}`).then(res => res.data);
export const tournamentStartPuzzle = () => api.post('/tournament/start').then(res => res.data);
export const tournamentAbandonPuzzle = () => api.post('/tournament/abandon').then(res => res.data);
export const tournamentSubmitSolution = () => api.post('/tournament/submit').then(res => res.data);
export const tournamentRejectPuzzle = () => api.post('/tournament/reject').then(res => res.data);
export const tournamentMakeMove = (x, y, value) => api.post('/tournament/move', { x, y, value }).then(res => res.data);
export const tournamentUndoMove = () => api.post('/tournament/undo').then(res => res.data);
export const tournamentEraseCell = (x, y) => api.post('/tournament/move', { x, y, value: null }).then(res => res.data);
export const tournamentGetHint = () => api.post('/tournament/hint').then(res => res.data);
export const tournamentSetNotes = (x, y, value) => api.post('/tournament/note', { x, y, value }).then(res => res.data);
export const tournamentDeleteNotes = (x, y) => api.delete('/tournament/note', { data: { x, y } }).then(res => res.data);
export const tournamentSetActive = (tournamentKey) => api.post(`/tournament/set/${tournamentKey}`).then(res => res.data);
export const tournamentGetTournaments = () => api.get('/tournament/tournaments').then(res => res.data);
export const tournamentGetDetail = (tournamentKey) => api.get(`/tournament/detail/${tournamentKey}`).then(res => res.data);
export const tournamentJoin = (shareToken) => api.post(`/tournament/join/${shareToken}`).then(res => res.data);
export const tournamentGetLeaderboard = (params = {}) => {
  const { skip, limit, tournament_key } = params;
  let url = '/tournament/leaderboard';
  const queryParams = new URLSearchParams();
  
  if (skip !== undefined) queryParams.append('skip', skip);
  if (limit !== undefined) queryParams.append('limit', limit);
  if (tournament_key !== undefined) queryParams.append('tournament_key', tournament_key);
  
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }
  
  return api.get(url).then(res => res.data);
};

// CREATE MODE
export const createGetStatus = () => api.get('/create/status').then(res => res.data);
export const createFindPuzzle = () => api.get('/create/find').then(res => res.data);
export const createStartPuzzle = () => api.post('/create/start').then(res => res.data);
export const createAbandonPuzzle = () => api.post('/create/abandon').then(res => res.data);
export const createSubmitSolution = () => api.post('/create/submit').then(res => res.data);
export const createRejectPuzzle = () => api.post('/create/reject').then(res => res.data);
export const createMakeMove = (x, y, value) => api.post('/create/cell/value', { x, y, value }).then(res => res.data);
export const createUndoMove = () => api.post('/create/undo').then(res => res.data);
export const createEraseCell = (x, y) => api.post('/create/cell/value', { x, y, value: null }).then(res => res.data);
export const createGetHint = () => api.post('/create/hint').then(res => res.data);
export const createSetNotes = (x, y, value) => api.post('/create/note', { x, y, value }).then(res => res.data);
export const createDeleteNotes = (x, y) => api.delete('/create/note', { data: { x, y } }).then(res => res.data);
export const createValidate = () => api.post('/create/validate').then(res => res.data);
export const createRevert = () => api.post('/create/revert').then(res => res.data);
export const createReveal = () => api.post('/create/cell/reveal').then(res => res.data);
export const createOperation = (operation) => api.post('/create/cell/operation', operation).then(res => res.data);
export const createDelete = () => api.post('/create/cell/delete').then(res => res.data);
export const createColor = (color) => api.post('/create/cell/color', { color }).then(res => res.data);
export const createSelect = (x, y) => api.post('/create/cell/select', { x, y }).then(res => res.data);
export const createBlank = (style, size) => api.post('/create/blank', { style, size }).then(res => res.data);
export const createSolved = (style, size) => api.post('/create/solved', { style, size }).then(res => res.data);
// the solver can take longer than the default 15 s timeout
export const createAuto = (style, size) => api.post('/create/auto', { style, size }, { timeout: 60000 }).then(res => res.data);
export const createShare = (puzzleKey) => api.post('/create/share', { puzzle_key: puzzleKey }).then(res => res.data);

// AUTH
export const registerDevice = (payload) => api.post('/auth/register', payload).then(res => res.data);
export const linkDevice = (payload) => api.post('/auth/link', payload).then(res => res.data);
export const unlinkDevice = (payload) => api.post('/auth/unlink', payload).then(res => res.data);
export const recoverAccount = (payload) => api.post('/auth/recover', payload).then(res => res.data);

// APP
export const getRegistrationData = (language) => api.get(`/app/registration_data?language=${language}`).then(res => res.data);
export const getAppMetadata = () => api.get(METADATA_ENDPOINT).then(res => res.data);

// USER
export const getUserProfile = () => api.get('/user/profile').then(res => res.data);
export const getUserFilters = () => api.get('/user/filters').then(res => res.data);
export const setUserFilters = (payload) => api.post('/user/filters', payload).then(res => res.data);
export const getUserLanguage = () => api.get('/user/language').then(res => res.data);
export const setUserLanguage = (language) => api.post('/user/language', {'language_code': language}).then(res => res.data);
export const setUserTheme = (theme) => api.post('/user/theme', {'theme': theme}).then(res => res.data);
export const setUserMode = (mode) => api.post('/user/mode', {'mode': mode}).then(res => res.data);
export const getUserBirthYear = () => api.get('/user/birth_year').then(res => res.data);
export const setUserBirthYear = (birthYear) => api.post('/user/birth_year', {'birth_year': birthYear}).then(res => res.data);
export const getUserGender = () => api.get('/user/gender').then(res => res.data);
export const setUserGender = (gender) => api.post('/user/gender', {'gender': gender}).then(res => res.data);
export const updateUserPreferences = (payload) => api.put('/user/preferences', payload).then(res => res.data);
export const getUserCreatedPuzzles = () => api.get('/create/created').then(res => res.data);
export const editCreatedPuzzle = (puzzleKey) => api.post('/create/edit', { puzzle_key: puzzleKey }).then(res => res.data);

// AD MONETIZATION
export const verifyAdCompletion = (adData) => api.post('/monetization/verify-ad', adData).then(res => res.data);
export const getRewardedTasksUrl = () => api.get('/monetization/rewarded-tasks-url').then(res => res.data);

// STRIPE MONETIZATION
export const createCheckoutSession = (items) => api.post('/monetization/create_checkout_session', { items }).then(res => res.data);

// NICKNAMES
export const getNicknames = () => api.get('/nicknames').then(res => res.data);
export const reserveNicknames = () => api.post('/nicknames/reserve').then(res => res.data);
export const setNickname = (nickname) => api.post('/nicknames/nickname', { nickname }).then(res => res.data);

// TOKENS
export const getTokenBalance = () => api.get('/tokens/balance').then(res => res.data);
export const claimTokens = () => api.post('/tokens/claim').then(res => res.data);

// INVITE
export const getUserInviteCode = () => api.get('/user/invite').then(res => res.data);

// MESSAGES
export const getMessageList = (skip = 0, limit = 20) => api.get(`/message/list?skip=${skip}&limit=${limit}`).then(res => res.data);
export const getMessageDetail = (messageKey) => api.get(`/message/${messageKey}`).then(res => res.data);
export const deleteMessage = (messageKey) => api.delete(`/message/${messageKey}`).then(res => res.data);

export const checkAuth = async () => {
  return !!(await storage.getItem('authToken'));
};